import { z } from "zod";

/**
 * `PIECE-BODY-CONTRACTS` (`P1-DOS-067`): **the bodies of the two doors that file a pièce's document**, one definition for the doors and for every client (A48). Strict, as `notifications-answer.ts` is: an
 * unknown key is a mismatch, not a surplus the door drops in silence. The doors parse their body with these before they touch a ticket (`invalid_request_body`, the field named).
 *
 * `declaredMonths` is the months THIS file holds (`PIECES-COUNT-ENFORCE`): a whole number from 1 to 12. The schema cannot know the pièce, so the door still refuses a count above the pièce's `expected_files` and
 * any declaration on a single-file pièce, in the same words.
 */
export const declaredMonthsSchema = z.number().int().min(1).max(12);

/**
 * `DEALER-DEPOSIT-DOOR`: **the body of the two upload-ticket doors**, `POST /api/v1/me/dossiers/{id}/pieces/{piece}/upload-ticket` (the buyer's) and `POST /api/v1/me/dealership/dossiers/{id}/pieces/{piece}/upload-ticket`
 * (the dealership's). It DOCUMENTS today's behaviour and changes none: the buyer's door has always read these three fields leniently (a missing or mistyped field reads `""` or `0`, and `documents`' ticket check then refuses
 * it in its own words), so the schema is loose and every field optional; the stubs of the dealer client speak it (A48).
 */
export const documentTicketBodySchema = z.looseObject({
  fileName: z.string().optional(),
  mimeType: z.string().optional(),
  sizeBytes: z.number().optional(),
});

/** `POST /api/v1/me/dossiers/{id}/pieces/{piece}/confirm`. */
export const documentConfirmBodySchema = z.strictObject({
  ticketId: z.string().min(1),
  declaredMonths: declaredMonthsSchema.optional(),
});

/** `POST /api/v1/dossiers/{id}/pieces/{piece}/on-behalf`. */
export const documentOnBehalfBodySchema = z.strictObject({
  /** Absent or empty is the DOMAIN's refusal (`missing_reception_channel`, 2.4.1's own sentence), not the shape's: the door's body check lets it through so that sentence is still the one said. */
  canal: z.string().optional(),
  note: z.string().optional(),
  ticketId: z.string().min(1),
  declaredMonths: declaredMonthsSchema.optional(),
});

export type DocumentTicketBody = z.infer<typeof documentTicketBodySchema>;
export type DocumentConfirmBody = z.infer<typeof documentConfirmBodySchema>;
export type DocumentOnBehalfBody = z.infer<typeof documentOnBehalfBodySchema>;

/**
 * `DEPOSIT-LINK-MONTHS` (`P0-DOS-069`): `POST /api/v1/public/deposit-links/{token}/confirm`, the public door the third of the three: `{ ticketId, declaredMonths? }`. The token is the credential and rides the path, never the
 * body. `declaredMonths` is what the other two doors take (a whole number from 1 to 12; the door still refuses a count above the pièce's `expected_files` and any declaration on a single-file pièce, in the same words).
 */
export const depositLinkConfirmBodySchema = z.strictObject({
  ticketId: z.string().min(1),
  declaredMonths: declaredMonthsSchema.optional(),
});
export type DepositLinkConfirmBody = z.infer<typeof depositLinkConfirmBodySchema>;

/**
 * `DEPOSIT-LINK-MONTHS`: what the public page reads of a link (`GET .../deposit-links/{token}` and the confirm's answer). `multiFile` and `expectedFiles` tell the page whether the pièce asks for several
 * documents (and how many months), so it can ask « Combien de mois ce fichier contient-il ? » for a combined PDF. Strict.
 */
const buildDepositLinkView = (object: typeof z.strictObject) =>
  object({
    label: z.string(),
    pieceCode: z.string(),
    state: z.enum(["ouvert", "utilise", "expire", "revoque"]),
    expiresAt: z.string(),
    supplier: z.enum(["client", "vendeur"]),
    multiFile: z.boolean(),
    /** How many months a `multi_file` pièce asks for; `null` for a pièce that asks for one document. */
    expectedFiles: z.number().int().min(2).nullable(),
    /**
     * `LINK-COVERED-MONTHS`: the months the pièce's LIVE files cover so far (`coveredMonthsOf`, a superseded file never counts), so the page can say « 2 mois sur 3 déjà reçus »; `null` for a pièce that asks for
     * one document. Additive.
     */
    coveredMonths: z.number().int().min(0).nullable(),
  });
export const depositLinkViewSchema = buildDepositLinkView(z.strictObject);
/** The public page's reading of the same view: unknown keys tolerated. */
export const depositLinkViewLenientReadSchema = buildDepositLinkView(z.looseObject);
export type DepositLinkView = z.infer<typeof depositLinkViewSchema>;
